'use client';

import { useCallback } from 'react';

import getBaseTrackingData from '~/analytics/getBaseTrackingData';
import trackEventAdobeAnalytics from '~/analytics/trackEventAdobeAnalytics';
import trackEventSegment from '~/analytics/trackEventSegment';
import { isWebmail } from '~/components/atoms/Analytics/utils/isWebmail';
import { getHasEnterpriseEmployeeCount } from '~/utils/reveal';

const useTrackCalendlyEvent = () => {
  const track = (eventName: string, properties: Record<string, string>) => {
    trackEventAdobeAnalytics(eventName, properties);
    trackEventSegment(eventName, properties);
  };
  // Function to fetch Calendly event data from our API route
  const fetchCalendlyEventData = async (
    eventUri: string,
    inviteeUri: string,
  ) => {
    try {
      const params = new URLSearchParams({
        eventUri,
        inviteeUri,
      });
      const response = await fetch(`/api/calendly?${params.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        console.error('Calendly API response not ok:', response.status);
        return null;
      }

      const data = await response.json();

      if (data.success && data.tracking) {
        return data.tracking;
      }

      return null;
    } catch (error) {
      console.error('Error calling Calendly API:', error);
      return null;
    }
  };

  const handleCalendlyEvent = useCallback((e: MessageEvent) => {
    const baseTrackingData = getBaseTrackingData();

    switch (e.data.event) {
      case 'calendly.event_type_viewed':
        track('calendly-loaded', {
          ...baseTrackingData,
        });
        break;
      case 'calendly.date_and_time_selected':
        track('calendly-meeting-date-selected', {
          ...baseTrackingData,
        });

        break;
      case 'calendly.event_scheduled':
        track('button-click', { title: 'Schedule Event', ...baseTrackingData });

        // Fetch real event and invitee data from Calendly API
        fetchCalendlyEventData(
          e.data.payload.event.uri,
          e.data.payload.invitee.uri,
        )
          .then(async (calendlyData) => {
            if (calendlyData) {
              // Track with real data
              track('calendly-meeting-scheduled', {
                ...baseTrackingData,
                ...calendlyData,
              });

              // send tracking events for business emails only
              if (!isWebmail(calendlyData.email)) {
                track('mgl-email-captured-client', {
                  ...baseTrackingData,
                  ...calendlyData,
                });

                const hasEnterpriseEmployeeCount =
                  await getHasEnterpriseEmployeeCount();

                if (hasEnterpriseEmployeeCount) {
                  track('mql-email-captured-client', {
                    ...baseTrackingData,
                    ...calendlyData,
                  });
                }
              }
            }
          })
          .catch((error) => {
            console.error('Error fetching Calendly data:', error);
          });

        break;
      default:
        break;
    }
  }, []);
  return { handleCalendlyEvent };
};

export default useTrackCalendlyEvent;
